<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('time.export_timesheet') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSubmit">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{includeEmployees: 'onlyCurrent'}"
                :label="$t('time.export_employee_hint')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.month"
                type="select"
                :label="$t('time.month')"
                :options="monthOptions"
                :rules="rules.month"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.year"
                type="select"
                :label="$t('general.year')"
                :options="yearOptions"
                :rules="rules.year"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.voucherAmount"
                :label="$t('time.voucher_amount')"
                :rules="rules.voucherAmount"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="exportButtonLabel"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div class="orangehrm-card-container timesheet-calendar-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('time.attendance_overview') }} - {{ monthLabel }}
        {{ filters.year?.id }}
      </oxd-text>

      <oxd-divider />

      <!-- Assenze in attesa di approvazione -->
      <pending-action-bar
        v-if="hasPendingLeave"
        :message="pendingLeaveMessage"
        :secondary="pendingLeaveSecondary"
        :action-label="$t('time.approve_all_pending')"
        :disabled="pendingLeave.eligible === 0 || isApproving"
        @action="showApprovalDialog = true"
      />

      <!-- Rimborsi in attesa di accettazione -->
      <pending-action-bar
        v-if="pendingClaims.count > 0"
        icon="💰"
        :message="pendingClaimsMessage"
        :secondary="claimTotalsLabel"
        :action-label="$t('time.accept_all_claims')"
        :disabled="isAcceptingClaims"
        @action="showClaimDialog = true"
      />

      <div v-if="isLoadingCalendar" class="calendar-loading">
        <oxd-loading-spinner />
        <oxd-text>{{ $t('time.loading_calendar') }}</oxd-text>
      </div>

      <div
        v-else-if="calendarData && calendarData.employees.length > 0"
        class="calendar-wrapper"
      >
        <div class="calendar-legend">
          <div class="legend-item">
            <span class="legend-color legend-ok"></span>
            <oxd-text tag="span">{{ $t('time.legend_ok') }}</oxd-text>
          </div>
          <div class="legend-item">
            <span class="legend-color legend-partial"></span>
            <oxd-text tag="span">{{ $t('time.legend_partial') }}</oxd-text>
          </div>
          <div class="legend-item">
            <span class="legend-color legend-missing"></span>
            <oxd-text tag="span">{{ $t('time.legend_missing') }}</oxd-text>
          </div>
          <div class="legend-item">
            <span class="legend-color legend-overtime"></span>
            <oxd-text tag="span">{{ $t('time.legend_overtime') }}</oxd-text>
          </div>
          <div class="legend-item">
            <span class="legend-color legend-weekend"></span>
            <oxd-text tag="span">{{ $t('time.legend_weekend') }}</oxd-text>
          </div>
          <div class="legend-item">
            <span class="legend-color legend-pending"></span>
            <oxd-text tag="span">{{
              $t('time.legend_pending_leave')
            }}</oxd-text>
          </div>
        </div>

        <div class="calendar-table-wrapper">
          <table class="calendar-table">
            <thead>
              <tr>
                <th class="employee-header">{{ $t('general.employee') }}</th>
                <th
                  v-for="day in calendarData.daysInMonth"
                  :key="day"
                  class="day-header oxd-table-header-cell"
                  :class="{'weekend-header': isDayNonWorking(day)}"
                >
                  {{ day }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="employee in calendarData.employees"
                :key="employee.empNumber"
              >
                <td class="employee-name">
                  {{ employee.firstName }} {{ employee.lastName }}
                  <span
                    v-if="employee.expenses"
                    class="expense-indicator"
                    :title="expenseTooltip(employee.expenses)"
                  >
                    💰
                  </span>
                </td>
                <td
                  v-for="dayData in employee.days"
                  :key="dayData.day"
                  class="day-cell oxd-table-header-cell"
                  :class="dayCellClass(dayData)"
                  :title="dayTooltip(dayData)"
                >
                  <template
                    v-if="dayData.workedHours > 0 && dayData.leaveHours > 0"
                  >
                    <span class="hours-indicator">
                      {{ formatHours(dayData.workedHours) }}
                    </span>
                    <span class="leave-indicator-small">
                      {{ formatHours(dayData.leaveHours) }}(A)
                    </span>
                  </template>
                  <template
                    v-else-if="
                      dayData.leaveHours > 0 && dayData.workedHours === 0
                    "
                  >
                    <span class="leave-indicator">A</span>
                  </template>
                  <template v-else-if="dayData.workedHours > 0">
                    <span class="hours-indicator">
                      {{ formatHours(dayData.workedHours) }}
                    </span>
                  </template>
                  <template
                    v-else-if="
                      dayData.standbyHours > 0 || dayData.interventionHours > 0
                    "
                  >
                    <span class="standby-indicator">R</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="calendar-empty">
        <oxd-text>{{ $t('time.no_data_for_period') }}</oxd-text>
      </div>
    </div>

    <approve-pending-leave-dialog
      v-if="showApprovalDialog"
      :eligible="pendingLeave.eligible"
      :items="pendingLeave.items"
      :is-loading="isApproving"
      @close="showApprovalDialog = false"
      @confirm="onApprovePendingLeave"
    />

    <approve-pending-claim-dialog
      v-if="showClaimDialog"
      :count="pendingClaims.count"
      :totals="pendingClaims.totals"
      :items="pendingClaims.items"
      :outside-period-count="pendingClaims.outsidePeriodCount"
      :is-loading="isAcceptingClaims"
      @close="showClaimDialog = false"
      @confirm="onAcceptClaims"
    />
  </div>
</template>

<script>
import {ref} from 'vue';
import {
  required,
  digitsOnlyWithDecimalPoint,
} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import useToast from '@/core/util/composable/useToast';
import ApprovePendingLeaveDialog from '@/orangehrmTimePlugin/components/ApprovePendingLeaveDialog';
import ApprovePendingClaimDialog from '@/orangehrmTimePlugin/components/ApprovePendingClaimDialog';
import PendingActionBar from '@/orangehrmTimePlugin/components/PendingActionBar';

const emptyClaims = () => ({
  count: 0,
  totals: {},
  outsidePeriodCount: 0,
  listed: 0,
  items: [],
});

const MONTH_KEYS = [
  'general.january',
  'general.february',
  'general.march',
  'general.april',
  'general.may',
  'general.june',
  'general.july',
  'general.august',
  'general.september',
  'general.october',
  'general.november',
  'general.december',
];

export default {
  name: 'ExportTimesheetReport',

  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'approve-pending-leave-dialog': ApprovePendingLeaveDialog,
    'approve-pending-claim-dialog': ApprovePendingClaimDialog,
    'pending-action-bar': PendingActionBar,
  },

  setup() {
    const toast = useToast();

    const isLoading = ref(false);
    const isLoadingCalendar = ref(false);
    const isApproving = ref(false);
    const showApprovalDialog = ref(false);
    const calendarData = ref(null);
    const pendingLeave = ref({eligible: 0, notEligible: 0, items: []});
    const isAcceptingClaims = ref(false);
    const showClaimDialog = ref(false);
    const pendingClaims = ref(emptyClaims());

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const calendarHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/timesheet-calendar',
    );
    const pendingLeaveHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/pending-leave-approvals',
    );
    const pendingClaimsHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/pending-claim-approvals',
    );
    const exportHttp = new APIService(
      window.appGlobal.baseUrl,
      '/time/exportTimesheetExcel',
    );

    return {
      toast,
      isLoading,
      isLoadingCalendar,
      isApproving,
      showApprovalDialog,
      calendarData,
      pendingLeave,
      currentMonth,
      currentYear,
      calendarHttp,
      pendingLeaveHttp,
      pendingClaimsHttp,
      exportHttp,
      isAcceptingClaims,
      showClaimDialog,
      pendingClaims,
    };
  },

  data() {
    return {
      filters: {
        employee: null,
        month: null,
        year: null,
        voucherAmount: '8.00',
      },
      calendarAbortController: null,
    };
  },

  computed: {
    rules() {
      return {
        employee: [],
        month: [],
        year: [],
        voucherAmount: [
          required,
          digitsOnlyWithDecimalPoint,
          (value) =>
            parseFloat(value) > 0 || this.$t('time.voucher_amount_invalid'),
        ],
      };
    },

    monthOptions() {
      return MONTH_KEYS.map((key, index) => ({
        id: index + 1,
        label: this.$t(key),
      }));
    },

    yearOptions() {
      const years = [];
      for (
        let year = this.currentYear - 5;
        year <= this.currentYear + 1;
        year++
      ) {
        years.push({id: year, label: String(year)});
      }
      return years;
    },

    monthLabel() {
      const month = this.filters.month?.id;
      return month ? this.$t(MONTH_KEYS[month - 1]) : '';
    },

    exportButtonLabel() {
      return this.filters.employee?.id
        ? this.$t('time.export_excel')
        : this.$t('time.export_all_zip');
    },

    pendingLeaveMessage() {
      return `${this.pendingLeave.eligible} ${this.$t(
        'time.pending_leave_count',
      )}`;
    },

    pendingLeaveSecondary() {
      return this.pendingLeave.notEligible > 0
        ? `${this.pendingLeave.notEligible} ${this.$t(
            'time.pending_leave_not_eligible',
          )}`
        : '';
    },

    pendingClaimsMessage() {
      return `${this.pendingClaims.count} ${this.$t(
        'time.pending_claim_count',
      )}`;
    },

    claimTotalsLabel() {
      return Object.entries(this.pendingClaims.totals ?? {})
        .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`)
        .join(' + ');
    },

    hasPendingLeave() {
      return (
        this.pendingLeave.eligible > 0 || this.pendingLeave.notEligible > 0
      );
    },

    queryParams() {
      const params = {
        month: this.filters.month?.id,
        year: this.filters.year?.id,
      };
      if (this.filters.employee?.id) {
        params.empNumber = this.filters.employee.id;
      }
      return params;
    },
  },

  created() {
    this.filters.month = {
      id: this.currentMonth,
      label: this.$t(MONTH_KEYS[this.currentMonth - 1]),
    };
    this.filters.year = {id: this.currentYear, label: String(this.currentYear)};
  },

  mounted() {
    this.refresh();

    this.$watch(
      () => [this.filters.month, this.filters.year, this.filters.employee],
      () => this.refresh(),
      {deep: true},
    );
  },

  methods: {
    refresh() {
      if (!this.filters.month?.id || !this.filters.year?.id) return;
      this.fetchCalendar();
      this.fetchPendingLeave();
      this.fetchPendingClaims();
    },

    async fetchCalendar() {
      // Annulla la richiesta precedente: senza questo, cambiando filtri in
      // fretta la risposta più lenta può sovrascrivere quella corrente.
      if (this.calendarAbortController) {
        this.calendarAbortController.abort();
      }
      this.calendarAbortController = new AbortController();
      const {signal} = this.calendarAbortController;

      this.isLoadingCalendar = true;
      try {
        // `getAll` accetta solo i parametri di query, quindi il signal
        // finirebbe nella URL come `signal=[object AbortSignal]` e l'API
        // rifiuterebbe la richiesta con 422. `request` prende la
        // configurazione axios completa, dove il signal va davvero.
        const response = await this.calendarHttp.request({
          method: 'GET',
          params: this.queryParams,
          signal,
        });
        if (signal.aborted) return;
        this.calendarData = response.data?.data ?? null;
      } catch (error) {
        if (signal.aborted) return;
        this.calendarData = null;
      } finally {
        if (!signal.aborted) {
          this.isLoadingCalendar = false;
        }
      }
    },

    async fetchPendingLeave() {
      try {
        const response = await this.pendingLeaveHttp.getAll(this.queryParams);
        this.pendingLeave = response.data?.data ?? {
          eligible: 0,
          notEligible: 0,
          items: [],
        };
      } catch (error) {
        this.pendingLeave = {eligible: 0, notEligible: 0, items: []};
      }
    },

    async fetchPendingClaims() {
      try {
        const response = await this.pendingClaimsHttp.getAll(this.queryParams);
        this.pendingClaims = response.data?.data ?? emptyClaims();
      } catch (error) {
        this.pendingClaims = emptyClaims();
      }
    },

    async onAcceptClaims() {
      this.isAcceptingClaims = true;
      try {
        const response = await this.pendingClaimsHttp.request({
          method: 'PUT',
          data: {data: this.queryParams},
        });
        this.showClaimDialog = false;

        this.toast.success(this.$t('time.claims_accepted'), '');

        // Il dialog annuncia un numero, il PUT ne accetta un altro se qualche
        // richiesta non era approvabile: dirlo invece di tacerlo.
        const skipped = response.data?.data?.skipped ?? 0;
        if (skipped > 0) {
          this.toast.warn(this.$t('time.claims_skipped'), '');
        }

        this.refresh();
      } finally {
        this.isAcceptingClaims = false;
      }
    },

    async onApprovePendingLeave() {
      this.isApproving = true;
      try {
        await this.pendingLeaveHttp.request({
          method: 'PUT',
          data: {data: this.queryParams},
        });
        this.showApprovalDialog = false;
        this.toast.success(this.$t('time.pending_leave_approved'), '');
        this.refresh();
      } finally {
        this.isApproving = false;
      }
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const response = await this.exportHttp.request({
          method: 'GET',
          responseType: 'blob',
          params: {
            employeeId: this.filters.employee?.id ?? '',
            month: this.filters.month?.id,
            year: this.filters.year?.id,
            voucherAmount: this.filters.voucherAmount,
          },
        });
        this.saveBlob(response.data, this.fileNameOf(response));
      } catch (error) {
        this.toast.error(this.$t('time.export_failed'), '');
      } finally {
        this.isLoading = false;
      }
    },

    saveBlob(blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    fileNameOf(response) {
      const header = response.headers?.['content-disposition'] ?? '';

      const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
      if (encoded) {
        try {
          return decodeURIComponent(encoded[1].trim());
        } catch (error) {
          // Header malformato: si ricade sul filename semplice.
        }
      }

      const plain = header.match(/filename="?([^";]+)"?/i);
      return plain ? plain[1].trim() : 'export.xlsx';
    },

    isDayNonWorking(day) {
      const employee = this.calendarData?.employees?.[0];
      if (!employee) {
        const date = new Date(
          this.filters.year.id,
          this.filters.month.id - 1,
          day,
        );
        const weekDay = date.getDay();
        return weekDay === 0 || weekDay === 6;
      }
      const dayData = employee.days.find((entry) => entry.day === day);
      return Boolean(dayData?.isWeekend || dayData?.isHoliday);
    },

    dayCellClass(dayData) {
      const classes = ['status-' + dayData.status];
      if (dayData.pendingLeaveHours > 0) {
        classes.push('has-pending-leave');
      }
      return classes;
    },

    dayTooltip(dayData) {
      const parts = [];

      if (dayData.isHoliday) {
        parts.push(this.$t('time.legend_weekend'));
      } else if (dayData.isWeekend) {
        parts.push(this.$t('time.legend_weekend'));
      }
      if (dayData.leaveHours > 0) {
        parts.push(
          `${this.$t('time.leave_hours')}: ${dayData.leaveHours.toFixed(2)}h` +
            (dayData.leaveType ? ` (${dayData.leaveType})` : ''),
        );
      }
      if (dayData.ordinaryHours > 0) {
        parts.push(
          `${this.$t('time.ordinary_hours')}: ${dayData.ordinaryHours.toFixed(
            2,
          )}h`,
        );
      }
      if (dayData.overtimeHours > 0) {
        parts.push(
          `${this.$t('time.overtime_hours')}: ${dayData.overtimeHours.toFixed(
            2,
          )}h`,
        );
      }
      if (dayData.standbyHours > 0) {
        parts.push(
          `${this.$t('time.standby_hours')}: ` +
            `${dayData.standbyHours.toFixed(2)}h`,
        );
      }
      if (dayData.interventionHours > 0) {
        parts.push(
          `${this.$t('time.intervention_hours')}: ` +
            `${dayData.interventionHours.toFixed(2)}h`,
        );
      }
      if (dayData.pendingLeaveHours > 0) {
        parts.push(
          `${this.$t('time.legend_pending_leave')}: ` +
            `${dayData.pendingLeaveHours.toFixed(2)}h`,
        );
      }
      if (dayData.status === 'missing') {
        parts.push(this.$t('time.legend_missing'));
      }

      return parts.join(' | ');
    },

    expenseTooltip(expenses) {
      if (!expenses) return '';
      return `${this.$t('time.reimbursements')}: ${
        expenses.count
      } (€${expenses.totalAmount.toFixed(2)})`;
    },

    // Mostra anche le frazioni sotto l'ora: una mezza giornata da 0,5h non
    // deve apparire come cella colorata vuota.
    formatHours(hours) {
      return hours > 0 ? String(Number(hours.toFixed(1))) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './time-reports.scss';
</style>
